import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Users,
  Plus,
  Minus,
  Calculator,
  ChefHat,
  Clock,
  RotateCcw,
  Loader2
} from 'lucide-react';
import { formatQty } from '../../../shared/culinary';
import { MEAL_PLAN_CONFIG } from '../../../config/mealPlan';
import { scaleRecipe } from 'wasp/client/operations';

interface RecipeIngredient {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  notes?: string;
}

interface RecipeStep {
  id: string;
  index: number;
  text: string;
  durationSeconds?: number;
}

interface Recipe {
  id: string;
  title: string;
  servings: number;
  totalMinutes?: number;
  difficulty?: string;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
}

interface RecipeScalerProps {
  recipe: Recipe;
  onScaledRecipe?: (scaledRecipe: Recipe & { scaleFactor: number }) => void;
  className?: string;
}

export function RecipeScaler({ recipe, onScaledRecipe, className }: RecipeScalerProps) {
  const [targetServings, setTargetServings] = useState(recipe.servings);
  const [scaledRecipe, setScaledRecipe] = useState<Recipe | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scaleFactor = targetServings / recipe.servings;
  const isScaled = scaleFactor !== 1;

  // Scale recipe using backend service (with mathjs precision)
  useEffect(() => {
    if (scaleFactor === 1) {
      setScaledRecipe(null);
      setError(null);
      return;
    }

    const fetchScaledRecipe = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await scaleRecipe({
          recipeId: recipe.id,
          targetServings,
          saveAsUserRecipe: false,
        });

        // Transform backend response to match Recipe interface
        const newScaledRecipe: Recipe = {
          id: result.scaledRecipe.id,
          title: result.scaledRecipe.title,
          servings: result.scaledRecipe.servings,
          totalMinutes: result.scaledRecipe.totalMinutes || undefined,
          difficulty: result.scaledRecipe.difficulty || undefined,
          ingredients: result.scaledRecipe.ingredients.map((ing: any) => ({
            id: ing.ingredientId,
            name: ing.name,
            quantity: ing.quantity,
            unit: ing.unit,
            notes: ing.notes,
          })),
          steps: recipe.steps, // Steps don't change in scaling
        };

        setScaledRecipe(newScaledRecipe);
        onScaledRecipe?.({ ...newScaledRecipe, scaleFactor: result.scaleFactor });
      } catch (err) {
        console.error('Failed to scale recipe:', err);
        setError(err instanceof Error ? err.message : 'Failed to scale recipe');
      } finally {
        setIsLoading(false);
      }
    };

    fetchScaledRecipe();
  }, [targetServings, recipe.id, recipe.steps, scaleFactor, onScaledRecipe]);

  const handleServingChange = (change: number) => {
    // Allow up to 100 servings to handle large-batch recipes
    const maxAllowed = Math.max(100, recipe.servings * 4); // At least 4x original or 100
    const newServings = Math.max(MEAL_PLAN_CONFIG.MIN_SERVINGS, Math.min(maxAllowed, targetServings + change));
    setTargetServings(newServings);
  };

  const resetToOriginal = () => {
    setTargetServings(recipe.servings);
  };

  const currentRecipe = scaledRecipe || recipe;
  const maxAllowed = Math.max(100, recipe.servings * 4);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-coral" />
          Recipe Scaler
          {isScaled && (
            <Badge variant="outline" className="ml-auto bg-coral/10 text-coral border-coral/20">
              Scaled {scaleFactor.toFixed(1)}x
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Serving Adjuster */}
        <div className="flex items-center justify-center gap-4 p-4 bg-cream rounded-lg">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleServingChange(-1)}
            disabled={targetServings <= MEAL_PLAN_CONFIG.MIN_SERVINGS || isLoading}
            className="h-10 w-10 p-0"
          >
            <Minus className="w-4 h-4" />
          </Button>

          <div className="text-center">
            <div className="flex items-center gap-2 text-xl font-bold text-coral">
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Users className="w-5 h-5" />
              )}
              {targetServings}
            </div>
            <div className="text-sm text-muted-foreground">
              {targetServings === 1 ? 'serving' : 'servings'}
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleServingChange(1)}
            disabled={targetServings >= maxAllowed || isLoading}
            className="h-10 w-10 p-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Scale Info */}
        {isScaled && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-sage/5 rounded-lg border border-sage/20">
            <div className="text-center">
              <div className="text-lg font-bold text-sage">{scaleFactor.toFixed(1)}x</div>
              <div className="text-sm text-muted-foreground">Scale Factor</div>
            </div>
            {currentRecipe.totalMinutes && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 text-lg font-bold text-sage">
                  <Clock className="w-4 h-4" />
                  {currentRecipe.totalMinutes}m
                </div>
                <div className="text-sm text-muted-foreground">Est. Time</div>
              </div>
            )}
            <div className="text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={resetToOriginal}
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </Button>
            </div>
          </div>
        )}

        {/* Scaled Ingredients */}
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
              <Loader2 className="w-8 h-8 animate-spin text-coral" />
            </div>
          )}
          <h3 className="font-medium text-lg mb-3 flex items-center gap-2">
            <ChefHat className="w-4 h-4" />
            Ingredients
            {isScaled && <span className="text-coral">(Scaled)</span>}
          </h3>
          <div className="space-y-2">
            {currentRecipe.ingredients.map((ingredient, index) => {
              const originalIngredient = recipe.ingredients[index];
              const isChanged = isScaled && (
                ingredient.quantity !== originalIngredient.quantity || 
                ingredient.unit !== originalIngredient.unit
              );
              
              return (
                <div 
                  key={ingredient.id}
                  className={`flex items-center justify-between p-3 rounded border ${
                    isChanged 
                      ? 'bg-coral/5 border-coral/20' 
                      : 'bg-white border-stone/20'
                  }`}
                >
                  <div className="flex-1">
                    <span className="font-medium">
                      {formatQty(ingredient.quantity, ingredient.unit)}
                    </span>
                    <span className="ml-2 text-muted-foreground">
                      {ingredient.name}
                    </span>
                    {ingredient.notes && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({ingredient.notes})
                      </span>
                    )}
                  </div>
                  
                  {isChanged && (
                    <div className="text-xs text-muted-foreground ml-4">
                      was: {formatQty(originalIngredient.quantity, originalIngredient.unit)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Scaling Notes */}
        {isScaled && (
          <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
            <h4 className="font-medium text-warning-800 mb-2">Scaling Notes:</h4>
            <ul className="text-sm text-warning-700 space-y-1 list-disc list-inside">
              <li>Ingredient quantities scaled with high-precision mathematics</li>
              <li>Seasonings (salt, spices) scaled gently using √ factor</li>
              <li>Cooking times may need manual adjustment based on your equipment</li>
              <li>Baking temperatures typically stay the same</li>
              <li>Always taste and adjust seasonings to your preference</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
